import React, { useState, useEffect, useRef } from 'react';
import { DomainResult } from '../../types/index';

interface PsychologicalAssessmentProps {
  onComplete: (result: DomainResult) => void;
  onNext: () => void;
}

interface PsychologicalResponses {
  depressedMood?: boolean; // 过去2周是否有抑郁情绪
  anhedonia?: boolean; // 过去2周是否有兴趣缺失
  phq2Question1?: number; // PHQ-2 第一题得分 (0-3) 为兼容性保留
  phq2Question2?: number; // PHQ-2 第二题得分 (0-3) 为兼容性保留
}

type PsychologicalStep = 
  | 'intro' 
  | 'question1'
  | 'question2'
  | 'results'
  | 'carer_correction' 
  | 'completed';

const PsychologicalAssessment: React.FC<PsychologicalAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<PsychologicalStep>('intro');
  const [responses, setResponses] = useState<Partial<PsychologicalResponses>>({});
  const hasCompletedRef = useRef<boolean>(false);
  const [carerCorrection, setCarerCorrection] = useState({
    depressedMoodCorrect: null as boolean | null,
    anhedoniaCorrect: null as boolean | null
  });

  // WHO ICOPE 标准问题 - 简化为Yes/No问题
  const questions = [
    {
      id: 'depressedMood',
      text: '过去两周，您是否因感到情绪低落、沉重或绝望而受到困扰？',
      description: '请根据您最近两周的真实感受回答'
    },
    {
      id: 'anhedonia', 
      text: '过去两周，您是否因对事物几乎没有兴趣或乐趣而受到困扰？',
      description: '包括对平时喜欢的活动也失去兴趣'
    }
  ];

  const responseOptions = [
    { value: false, label: '没有，我没有这些问题', color: 'green' },
    { value: true, label: '是的，我有这些困扰', color: 'red' }
  ];

  // 评估逻辑实现 - 按WHO ICOPE标准
  const evaluatePsychological = (): DomainResult => {
    const hasDepressedMood = responses.depressedMood === true;
    const hasAnhedonia = responses.anhedonia === true;

    // WHO ICOPE 标准：任一问题回答'是'即为失败
    const hasDepressionSymptoms = hasDepressedMood || hasAnhedonia;
    const passed = !hasDepressionSymptoms;
    
    const testResults = [
      { 
        name: "抑郁情绪评估", 
        passed: !hasDepressedMood,
        response: hasDepressedMood,
        details: "询问过去2周是否受情绪低落困扰",
        rawUserAnswers: {
          depressedMood: responses.depressedMood,
          hasDepressedMood: hasDepressedMood
        }
      },
      { 
        name: "兴趣缺失评估", 
        passed: !hasAnhedonia,
        response: hasAnhedonia,
        details: "询问过去2周是否受兴趣丧失困扰",
        rawUserAnswers: {
          anhedonia: responses.anhedonia,
          hasAnhedonia: hasAnhedonia
        }
      }
    ];

    return {
      status: passed ? 'PASS' : 'FAIL',
      completionMethod: 'full_test',
      testResults,
      recommendations: passed ? 
        ["ICOPE-C3-S3.1.3-Advice-MentalHealth"] : 
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C10-S10.2-Community-Care-Depression"]
    };
  };

  const handleQuestionResponse = (questionId: 'depressedMood' | 'anhedonia', value: boolean) => {
    const updatedResponses = { ...responses, [questionId]: value };
    setResponses(updatedResponses);

    if (questionId === 'depressedMood') {
      setCurrentStep('question2');
    } else {
      setCurrentStep('results');
    }
  };

  const handleResultsConfirm = () => {
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      const result = evaluatePsychological();
      onComplete(result);
      setCurrentStep('completed');
    }
  };

  const handleCarerCorrection = (depressedMoodCorrect: boolean, anhedoniaCorrect: boolean) => {
    // 基于照护者修正重新评估心理健康状况
    // WHO ICOPE标准：任一维度有问题即为心理健康需要关注
    const hasDepressedMood = !depressedMoodCorrect;
    const hasAnhedonia = !anhedoniaCorrect;
    const hasDepressionSymptoms = hasDepressedMood || hasAnhedonia;
    const passed = !hasDepressionSymptoms;
    
    const correctedResult: DomainResult = {
      status: passed ? 'PASS' : 'FAIL',
      completionMethod: 'carer_corrected',
      testResults: [
        { 
          name: "抑郁情绪评估", 
          passed: depressedMoodCorrect,
          response: !depressedMoodCorrect,
          details: "照护者确认过去2周情绪状态",
          carerCorrected: true,
          rawUserAnswers: {
            depressedMood: !depressedMoodCorrect,
            hasDepressedMood: !depressedMoodCorrect,
            carerCorrected: true
          }
        },
        { 
          name: "兴趣缺失评估", 
          passed: anhedoniaCorrect,
          response: !anhedoniaCorrect,
          details: "照护者确认过去2周兴趣状态",
          carerCorrected: true,
          rawUserAnswers: {
            anhedonia: !anhedoniaCorrect,
            hasAnhedonia: !anhedoniaCorrect,
            carerCorrected: true
          }
        }
      ],
      recommendations: passed ? 
        ["ICOPE-C3-S3.1.3-Advice-MentalHealth"] : 
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C10-S10.2-Community-Care-Depression"]
    };
    
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      onComplete(correctedResult);
      setCurrentStep('completed');
    }
  };

  useEffect(() => {
    console.log('PsychologicalAssessment useEffect triggered, currentStep:', currentStep, 'hasCompleted:', hasCompletedRef.current);
    
    if (currentStep === 'completed' && !hasCompletedRef.current) {
      // 如果直接跳到completed但还没有完成评估，执行默认评估
      hasCompletedRef.current = true;
      const result = evaluatePsychological();
      console.log('Psychological evaluation result:', result);
      onComplete(result);
    }
    
    if (currentStep === 'completed') {
      const timer = setTimeout(() => {
        console.log('Timer expired, calling onNext');
        onNext();
      }, 2000);
      
      return () => {
        console.log('Cleaning up timer');
        clearTimeout(timer);
      };
    }
  }, [currentStep]);

  const renderIntro = () => (
    <div className="text-center space-y-4 p-4">
      <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">心理健康评估</h2>
      <p className="text-gray-600">评估您最近的情绪状态和心理健康</p>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
        <h3 className="font-semibold text-gray-900 mb-2">关于此评估：</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• 基于WHO ICOPE标准心理健康筛查</li>
          <li>• 仅包含2个简单的是/否问题</li>
          <li>• 评估过去两周的情绪状态</li>
          <li>• 请诚实回答，这有助于更好地了解您的健康状况</li>
        </ul>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
        <p className="text-sm text-yellow-800">
          <strong>隐私保护：</strong> 您的回答仅用于健康评估，不会泄露给任何第三方
        </p>
      </div>
      
      <button 
        onClick={() => setCurrentStep('question1')} 
        className="w-full bg-pink-600 text-white py-3 rounded-lg hover:bg-pink-700 transition-colors"
      >
        开始评估
      </button>
    </div>
  );

  const renderQuestion = (questionIndex: number) => {
    const question = questions[questionIndex];
    const isQuestion1 = questionIndex === 0;
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-pink-600">{questionIndex + 1}</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">心理健康评估</h2>
          <p className="text-gray-600">问题 {questionIndex + 1} / 2</p>
        </div>

        <div className="bg-pink-50 border border-pink-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{question.text}</h3>
          <p className="text-sm text-gray-600 mb-6">{question.description}</p>
          
          <div className="space-y-3">
            {responseOptions.map((option) => {
              const colorClasses = {
                green: 'bg-green-50 border-green-200 hover:bg-green-100 text-green-800',
                yellow: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100 text-yellow-800',
                orange: 'bg-orange-50 border-orange-200 hover:bg-orange-100 text-orange-800',
                red: 'bg-red-50 border-red-200 hover:bg-red-100 text-red-800'
              };
              
              return (
                <button
                  key={String(option.value)}
                  onClick={() => handleQuestionResponse(
                    isQuestion1 ? 'depressedMood' : 'anhedonia', 
                    option.value
                  )}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${colorClasses[option.color as keyof typeof colorClasses]}`}
                >
                  <span className="font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="text-center">
          <div className="flex justify-center space-x-2">
            {[0, 1].map((i) => (
              <div 
                key={i} 
                className={`w-3 h-3 rounded-full ${
                  i === questionIndex ? 'bg-pink-600' : 'bg-gray-300'
                }`} 
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const hasDepressedMood = responses.depressedMood === true;
    const hasAnhedonia = responses.anhedonia === true;
    const hasDepressionSymptoms = hasDepressedMood || hasAnhedonia;
    const passed = !hasDepressionSymptoms;
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            passed ? 'bg-green-100' : 'bg-yellow-100'
          }`}>
            <svg className={`w-8 h-8 ${passed ? 'text-green-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">评估完成</h2>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">WHO ICOPE 心理健康筛查结果：</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-3 bg-white rounded border">
              <div className={`text-lg font-bold ${hasDepressedMood ? 'text-red-600' : 'text-green-600'}`}>
                {hasDepressedMood ? '有困扰' : '无问题'}
              </div>
              <p className="text-sm text-gray-600">抑郁情绪</p>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className={`text-lg font-bold ${hasAnhedonia ? 'text-red-600' : 'text-green-600'}`}>
                {hasAnhedonia ? '有困扰' : '无问题'}
              </div>
              <p className="text-sm text-gray-600">兴趣缺失</p>
            </div>
          </div>

          <div className={`p-4 rounded-lg ${
            passed ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center space-x-2">
              <svg className={`w-5 h-5 ${passed ? 'text-green-600' : 'text-yellow-600'}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className={`font-medium ${passed ? 'text-green-800' : 'text-yellow-800'}`}>
                {passed ? '心理状态良好' : '建议关注心理健康'}
              </span>
            </div>
            <p className={`mt-2 text-sm ${passed ? 'text-green-700' : 'text-yellow-700'}`}>
              {passed 
                ? '您的心理状态表现良好，请继续保持积极乐观的生活态度' 
                : '检测到抑郁症状，建议咨询心理健康专业人员，获得进一步的评估和支持'}
            </p>
          </div>
          
          {!passed && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-800 mb-2">建议：</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• 考虑寻求专业心理健康服务</li>
                <li>• 与信任的朋友或家人分享感受</li>
                <li>• 保持规律的运动和良好的睡眠</li>
                <li>• 参与社交活动和有意义的爱好</li>
              </ul>
            </div>
          )}
          
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={handleResultsConfirm}
              className="w-full bg-pink-600 text-white py-3 px-6 rounded-lg hover:bg-pink-700 transition-colors font-medium"
            >
              确认结果，完成测试
            </button>
            <button
              onClick={() => setCurrentStep('carer_correction')}
              className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>照护者修正答案</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCarerCorrection = () => {
    const hasDepressedMood = responses.depressedMood === true;
    const hasAnhedonia = responses.anhedonia === true;
    const hasDepressionSymptoms = hasDepressedMood || hasAnhedonia;
    const passed = !hasDepressionSymptoms;
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
          <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">系统评估结果回顾</h3>
            
            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className={`text-center p-3 rounded border ${
                  hasDepressedMood ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div className={`text-lg font-bold ${hasDepressedMood ? 'text-red-600' : 'text-green-600'}`}>
                    {hasDepressedMood ? '有困扰 ❌' : '无问题 ✅'}
                  </div>
                  <p className="text-sm text-gray-600">抑郁情绪</p>
                </div>
                <div className={`text-center p-3 rounded border ${
                  hasAnhedonia ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div className={`text-lg font-bold ${hasAnhedonia ? 'text-red-600' : 'text-green-600'}`}>
                    {hasAnhedonia ? '有困扰 ❌' : '无问题 ✅'}
                  </div>
                  <p className="text-sm text-gray-600">兴趣缺失</p>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg text-center ${passed ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <span className={`font-medium ${passed ? 'text-green-800' : 'text-yellow-800'}`}>
                  系统评估：{passed ? '✅ 心理状态良好' : '❌ 建议关注心理健康'}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">照护者确认</h3>
            <p className="text-gray-700 mb-4">
              请照护者根据对老年人过去两周的观察和了解，确认以下各项的实际情况：
            </p>
            
            <div className="space-y-4">
              {/* 抑郁情绪确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">抑郁情绪状况确认</h4>
                <p className="text-sm text-gray-600 mb-3">过去两周，是否观察到老年人有情绪低落、沉重或绝望的表现？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, depressedMoodCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.depressedMoodCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.depressedMoodCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.depressedMoodCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 情绪状态正常，没有明显抑郁表现</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, depressedMoodCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.depressedMoodCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.depressedMoodCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.depressedMoodCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 有明显的情绪低落或抑郁表现</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 兴趣缺失确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">兴趣缺失状况确认</h4>
                <p className="text-sm text-gray-600 mb-3">过去两周，是否观察到老年人对事物缺乏兴趣或乐趣？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, anhedoniaCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.anhedoniaCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.anhedoniaCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.anhedoniaCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 兴趣正常，对活动保持积极态度</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, anhedoniaCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.anhedoniaCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.anhedoniaCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.anhedoniaCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 明显缺乏兴趣，对活动缺乏热情</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => handleCarerCorrection(carerCorrection.depressedMoodCorrect!, carerCorrection.anhedoniaCorrect!)}
            disabled={carerCorrection.depressedMoodCorrect === null || carerCorrection.anhedoniaCorrect === null}
            className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 transition-colors font-medium"
          >
            确认修正，完成评估
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="text-center p-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">心理健康评估完成</h2>
      <p className="text-gray-600">正在转到下一项...</p>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'intro': return renderIntro();
      case 'question1': return renderQuestion(0);
      case 'question2': return renderQuestion(1);
      case 'results': return renderResults();
      case 'carer_correction': return renderCarerCorrection();
      case 'completed': return renderCompleted();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {renderStep()}
      </div>
    </div>
  );
};

export default PsychologicalAssessment;